import { tv, type VariantProps } from 'tailwind-variants'
import { CheckIcon } from '_/ui/field/check-icon'
import { Label } from './label'
import { RegisterInputRef } from '_/interfaces'
import { useEffect, useRef, forwardRef } from 'react'

export const styles = tv({
	base: 'text-base placeholder-gray-200 placeholder font-normal rounded border px-6 py-3 w-full focus:ring-2 focus:ring-primary-neutral [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
	variants: {
		blocked: {
			true: 'opacity-50 pointer-events-none',
		},
		themeColor: {
			default: 'text-gray-300 border-primary-light hover:border-gray-700',
			invalid: 'text-red-500 border-red-500',
			success: 'text-primary-neutral border-primary-neutral',
		},
	},
	defaultVariants: {
		themeColor: 'default',
	},
})

export interface InputProps extends VariantProps<typeof styles>, React.InputHTMLAttributes<HTMLInputElement> {
	id?: string
	name: string
	className?: string
	label?: string
	blocked?: boolean
	hasCheckIcon?: boolean
	onInputChange?: (inputName: string, newValue: any, e?: React.ChangeEvent<HTMLInputElement>) => void
	registerInputRef?: RegisterInputRef
}

const InputComponent = forwardRef<HTMLInputElement, InputProps>(
	(
		{ className, blocked = false, themeColor, onInputChange, label, hasCheckIcon = true, registerInputRef, ...rest },
		ref,
	) => {
		const inputEl = useRef<HTMLInputElement | null>(null)

		useEffect(() => {
			if (registerInputRef && inputEl.current) {
				registerInputRef(rest.name, inputEl.current, 'personal-information')
			}
		}, [registerInputRef, rest.name])

		const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			if (onInputChange) {
				onInputChange(rest.name, e.target.value, e)
			}
		}

		return (
			<>
				{label && <Label htmlFor={rest.name}>{label}</Label>}
				<div className='relative'>
					<input
						{...rest}
						onChange={e => handleInputChange(e)}
						onBlur={e => handleInputChange(e)}
						className={styles({ class: className, themeColor, blocked })}
						ref={ref || inputEl}
					/>
					{themeColor && hasCheckIcon ? (
						<CheckIcon className={`${themeColor === 'success' ? 'block' : 'hidden'}`} />
					) : null}
				</div>
			</>
		)
	},
)

InputComponent.displayName = 'InputComponent'

export const Input = InputComponent
